// src/components/RelatorioEstagioModal.js
import React, { useState, useEffect, useMemo } from 'react';
import { Modal } from './Modal';
import { Button } from './Button';
import { formatarData } from '../utils/helpers';
import { useAuth } from '../context/AuthContext';

// --- Função de Máscara para Telefone ---
const formatPhoneNumber = (value) => {
    if (!value) return "";
    
    // Remove tudo que não é dígito
    const phoneNumber = value.replace(/[^\d]/g, "");
    const phoneNumberLength = phoneNumber.length;

    if (phoneNumberLength < 3) return phoneNumber;
    if (phoneNumberLength < 8) {
        return `(${phoneNumber.slice(0, 2)}) ${phoneNumber.slice(2)}`;
    }
    return `(${phoneNumber.slice(0, 2)}) ${phoneNumber.slice(2, 7)}-${phoneNumber.slice(7, 11)}`;
};

const RatingScale = ({ question, name, value, onChange, comments, onCommentsChange, required = true, isDisabled = false }) => {
    const scale = Array.from({ length: 11 }, (_, i) => i); // 0 a 10
    
    return (
        <div className="border p-4 rounded-lg bg-gray-50 space-y-3">
            <label className="block text-sm font-bold text-gray-700">{question}</label>
            <div className="flex justify-between items-center bg-white p-3 rounded-lg shadow-sm border">
                <span className="text-xs font-semibold text-red-600 w-1/4 text-left">Muito ruim ou inexistente (0)</span>
                
                <div className="flex space-x-2">
                    {scale.map(i => (
                        <label key={i} className="flex flex-col items-center cursor-pointer">
                            <input
                                type="radio"
                                name={name}
                                value={i}
                                checked={value === i}
                                onChange={onChange}
                                required={required}
                                disabled={isDisabled}
                                className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                            />
                            <span className="mt-1 text-xs font-medium text-gray-600">{i}</span>
                        </label>
                    ))}
                </div>

                <span className="text-xs font-semibold text-green-600 w-1/4 text-right">Excelente (10)</span>
            </div>
            
            <textarea
                name={`${name}_comentario`}
                rows="2"
                value={comments || ''}
                onChange={onCommentsChange}
                disabled={isDisabled}
                className="w-full border p-2 rounded text-sm mt-2"
                placeholder="Comentários sobre o quesito acima (Opcional)"
            ></textarea>
        </div>
    );
};

export const RelatorioEstagioModal = ({ show, onClose, escolha, relatorioExistente, handleSaveRelatorio, processing, error, handleGerarLinkPreceptor, reportSuccess, whatsappLink }) => {
    const { userProfile } = useAuth();
    const [activeForm, setActiveForm] = useState('cadete');
    const [formDataCadete, setFormDataCadete] = useState({});
    const [formDataPreceptor, setFormDataPreceptor] = useState({});
    
    // Estado para a mensagem específica de salvamento parcial
    const [partialSaveMessage, setPartialSaveMessage] = useState('');
    
    // Verifica se o usuário atual é um Cadete (comum ou acadêmico) ou Escalante
    const isCadeteOrEscalante = userProfile?.nivel === 'cadete' || userProfile?.nivel === 'cadete_academico' || userProfile?.nivel === 'escalante';

    const ratingQuestions = [
        { q: '7) Numa escala de 0 a 10, classifique o Aluno Oficial quanto à DISCIPLINA apresentada durante o estágio.', name: 'disciplina' },
        { q: '8) Numa escala de 0 a 10, classifique o Aluno Oficial quanto à PONTUALIDADE apresentada durante o estágio.', name: 'pontualidade' },
        { q: '9) Numa escala de 0 a 10, classifique o Aluno Oficial quanto à APRESENTAÇÃO PESSOAL apresentada durante o estágio.', name: 'apresentacao_pessoal' },
        { q: '10) Numa escala de 0 a 10, classifique o Aluno Oficial quanto à INTERAÇÃO COM A TROPA apresentada durante o estágio.', name: 'interacao_tropa' },
        { q: '11) Numa escala de 0 a 10, classifique o Aluno Oficial quanto à INTERAÇÃO COM O PÚBLICO apresentada durante o estágio.', name: 'interacao_publico' },
        { q: '12) Numa escala de 0 a 10, classifique o Aluno Oficial quanto à RESPONSABILIDADE apresentada durante o estágio.', name: 'responsabilidade' },
        { q: '13) Numa escala de 0 a 10, classifique o Aluno Oficial quanto à DEDICAÇÃO e INTERESSE apresentados durante o estágio.', name: 'dedicacao_interesse' },
        { q: '14) Numa escala de 0 a 10, classifique o Aluno Oficial quanto à INICIATIVA apresentada durante o estágio.', name: 'iniciativa' },
        { q: '15) Numa escala de 0 a 10, classifique o Aluno Oficial quanto à CAPACIDADE DE DECISÃO apresentada durante o estágio.', name: 'capacidade_decisao' },
        { q: '16) Numa escala de 0 a 10, classifique o Aluno Oficial quanto ao COMPROMETIMENTO COM A INSTITUIÇÃO apresentado durante o estágio.', name: 'comprometimento_instituicao' },
        { q: '17) Numa escala de 0 a 10, classifique o Aluno Oficial quanto a TRAÇOS DE LIDERANÇA apresentados durante o estágio.', name: 'tracos_lideranca' },
        { q: '18) Numa escala de 0 a 10, classifique o Aluno Oficial quanto ao CONHECIMENTO TÉCNICO-PROFISSIONAL apresentado durante o estágio.', name: 'conhecimento_tecnico' },
        { q: '19) Numa escala de 0 a 10, classifique o Aluno Oficial quanto à ESTABILIDADE EMOCIONAL apresentada durante o estágio.', name: 'estabilidade_emocional' },
        { q: '20) Numa escala de 0 a 10, classifique o Aluno Oficial quanto ao CONHECIMENTO JURÍDICO apresentado durante o estágio.', name: 'conhecimento_juridico' },
        { q: '21) Numa escala de 0 a 10, classifique o Aluno Oficial quanto à AUTOCONFIANÇA apresentada durante o estágio.', name: 'autoconfianca' },
    ];

    // Verifica se os dados essenciais do cadete estão preenchidos para liberar o link.
    const hasCadeteInitialData = useMemo(() => {
        const c = relatorioExistente?.cadete || formDataCadete;
        // Critério: Região, Horário de início, Horário de término, QRA Preceptor e Telefone Preceptor (com 11 dígitos) devem estar preenchidos.
        const isPhoneValid = c.telefone_preceptor && c.telefone_preceptor.replace(/\D/g, '').length === 11;
        return !!c.regiao && !!c.hr_inicio && !!c.hr_termino && !!c.qra_preceptor && isPhoneValid;
    }, [relatorioExistente, formDataCadete]);

    useEffect(() => {
        if (show && escolha) {
            const initialCadete = {
                regiao: escolha.local_nome || '', 
                data_estagio: formatarData(escolha.data) || '', 
                hr_inicio: '',
                hr_termino: '',
                qra_preceptor: '',
                telefone_preceptor: '',
                periodo: escolha.turno || 'diurno', 
                
                modelo_vtr: '',
                prefixo: '',
                area_conhecimento: '',
                justificativa_area: '',
                acontecimento_importante: '',
                indicacao_preceptor: '',
                dificuldades_solucoes: '',
                indicaria_estagio: '', 
            };
            
            const initialPreceptor = {
                email: '',
                posto_nome: '',
                telefone: '',
                data_estagio: formatarData(escolha.data) || '',
                funcao_executada: '',
                periodo: escolha.turno || 'diurno',
                ...Object.fromEntries(ratingQuestions.flatMap(q => [
                    [q.name, null], 
                    [`${q.name}_comentario`, '']
                ])),
                caracteristicas_positivas: '',
                deficiencias_dificuldades: '',
                nota_geral: null,
            };

            if (relatorioExistente) {
                setFormDataCadete(relatorioExistente.cadete || initialCadete);
                setFormDataPreceptor(relatorioExistente.preceptor || initialPreceptor);
                
                // Se for cadete ou escalante, força a aba 'cadete' se não tiver avaliação ou se tiver (para não mostrar)
                if (isCadeteOrEscalante) {
                    setActiveForm('cadete');
                } else {
                    setActiveForm(relatorioExistente.preceptor?.posto_nome ? 'preceptor' : 'cadete');
                }
            } else {
                setFormDataCadete(initialCadete);
                setFormDataPreceptor(initialPreceptor);
                setActiveForm('cadete');
            }
        }
        setPartialSaveMessage(''); // Limpa mensagem ao abrir
    }, [show, escolha, relatorioExistente, isCadeteOrEscalante]);

    const handleCadeteChange = (e) => {
        const { name, value } = e.target;
        let finalValue = value;
        
        // Aplica a máscara no telefone
        if (name === 'telefone_preceptor') {
             finalValue = formatPhoneNumber(value);
        }
        
        setFormDataCadete(prev => ({ ...prev, [name]: finalValue }));
    };
    
    // Validação de Campos Obrigatórios (Dados do Estágio)
    const validateRequiredFields = () => {
        const { regiao, hr_inicio, hr_termino, qra_preceptor, telefone_preceptor, periodo } = formDataCadete;
        
        if (!regiao || !hr_inicio || !hr_termino || !qra_preceptor || !telefone_preceptor || !periodo) {
            alert("Atenção: Todos os campos em 'DADOS DO ESTÁGIO' são obrigatórios. Por favor, preencha: Região, Horários, Qra do Preceptor, Telefone e Período.");
            return false;
        }
        return true;
    };

    // Validação de formato de telefone (11 dígitos)
    const validateTelefone = () => {
        const phone = formDataCadete.telefone_preceptor || '';
        const digits = phone.replace(/\D/g, '');
        if (digits.length !== 11) {
            alert("Atenção: O telefone do Oficial Preceptor deve conter exatamente 11 dígitos (DDD + 9 números) para salvar.");
            return false;
        }
        return true;
    };

    const handleCadeteSubmit = (e) => {
        e.preventDefault();
        if (!validateRequiredFields()) return;
        if (!validateTelefone()) return;

        handleSaveRelatorio({ cadete: formDataCadete, preceptor: formDataPreceptor });
        setPartialSaveMessage('');
    };

    const handleSalvarDadosIniciais = async (e) => {
        e.preventDefault(); 
        
        if (!validateRequiredFields()) return;
        if (!validateTelefone()) return;

        await handleSaveRelatorio({ cadete: formDataCadete, preceptor: formDataPreceptor });
        
        setPartialSaveMessage("✅ Dados do estágio salvos com sucesso! \n⚠️ ATENÇÃO: Não se esqueça de voltar e preencher as 'Observações' após a realização do estágio.");
        
        const msgElement = document.getElementById('partial-save-msg');
        if(msgElement) msgElement.scrollIntoView({ behavior: 'smooth' });
    };

    const renderCadeteForm = () => (
        <form onSubmit={handleCadeteSubmit} className="space-y-6">
            <h3 className="text-xl font-semibold text-blue-600 mb-4">DADOS DO ESTÁGIO</h3>
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700">1) Região (Batalhão/Local) *</label>
                    <input name="regiao" value={formDataCadete.regiao || ''} onChange={handleCadeteChange} required className="w-full border p-2 rounded" placeholder="Ex: 14º BPM/M" />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">2) Data *</label>
                    <input name="data_estagio" value={formDataCadete.data_estagio || ''} readOnly className="w-full border p-2 rounded bg-gray-100" />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">3) Horário de início *</label>
                    <input name="hr_inicio" type="time" value={formDataCadete.hr_inicio || ''} onChange={handleCadeteChange} required className="w-full border p-2 rounded" />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">4) Horário de término *</label>
                    <input name="hr_termino" type="time" value={formDataCadete.hr_termino || ''} onChange={handleCadeteChange} required className="w-full border p-2 rounded" />
                </div>
                
                <div>
                    <label className="block text-sm font-medium text-gray-700">5) Qra do Oficial Preceptor *</label>
                    <input name="qra_preceptor" value={formDataCadete.qra_preceptor || ''} onChange={handleCadeteChange} required className="w-full border p-2 rounded" placeholder="Ex: 1º Ten PM Silva" />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Telefone do Oficial Preceptor *</label>
                    <input 
                        name="telefone_preceptor" 
                        value={formDataCadete.telefone_preceptor || ''} 
                        onChange={handleCadeteChange} 
                        required
                        className="w-full border p-2 rounded" 
                        placeholder="(XX) XXXXX-XXXX" 
                        maxLength="15" 
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">6) Período *</label>
                    <select name="periodo" value={formDataCadete.periodo || 'diurno'} onChange={handleCadeteChange} required className="w-full border p-2 rounded">
                        <option value="diurno">Diurno</option>
                        <option value="noturno">Noturno</option>
                    </select>
                </div>

                <div className="flex items-end">
                    <Button 
                        type="button" 
                        onClick={handleSalvarDadosIniciais} 
                        variant="primary" 
                        className="bg-blue-500 hover:bg-blue-600 w-full"
                        disabled={processing}
                    >
                        Salvar Dados do Estágio
                    </Button>
                </div>
            </div>

            {partialSaveMessage && (
                <div id="partial-save-msg" className="mt-3 p-4 bg-yellow-50 border-l-4 border-yellow-500 text-yellow-800 rounded shadow-sm animate-fade-in">
                    <p className="font-bold text-sm whitespace-pre-line">{partialSaveMessage}</p>
                </div>
            )}

            <h3 className="text-xl font-semibold text-blue-600 mb-4 pt-4 border-t">OBSERVAÇÕES A RESPEITO DO ESTÁGIO</h3>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700">1) MODELO DE VTR</label>
                    <select name="modelo_vtr" value={formDataCadete.modelo_vtr || ''} onChange={handleCadeteChange} className="w-full border p-2 rounded">
                        <option value="">Selecione...</option>
                        <option value="TOYOTA HILUX">TOYOTA HILUX</option>
                        <option value="GM SPIN">GM SPIN</option>
                        <option value="VW SPACEFOX">VW SPACEFOX</option>
                        <option value="RENAULT DUSTER">RENAULT DUSTER</option>
                        <option value="GM TRAIL BLAZER">GM TRAIL BLAZER</option>
                        <option value="FIAT PALIO WEEKEND">FIAT PALIO WEEKEND</option>
                        <option value="Outro">Outro</option>
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">2) PREFIXO (Ex: M-43001)</label>
                    <input name="prefixo" value={formDataCadete.prefixo || ''} onChange={handleCadeteChange} className="w-full border p-2 rounded" />
                </div>
                <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700">3. Em que área de conhecimento acadêmico este estágio mais contribuiu para sua formação?</label>
                    <select name="area_conhecimento" value={formDataCadete.area_conhecimento || ''} onChange={handleCadeteChange} className="w-full border p-2 rounded">
                        <option value="">Selecione...</option>
                        <option value="Institucional">Institucional</option>
                        <option value="Tecnico-Policial">Técnico-Policial</option>
                        <option value="Juridica">Jurídica</option>
                    </select>
                </div>
                <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700">4) Justifique a resposta anterior.</label>
                    <textarea name="justificativa_area" rows="3" value={formDataCadete.justificativa_area || ''} onChange={handleCadeteChange} className="w-full border p-2 rounded"></textarea>
                </div>
            </div>
            
            <div>
                <label className="block text-sm font-medium text-gray-700">5. Comente um acontecimento importante que ocorreu durante o estágio e que chamou sua atenção.</label>
                <textarea name="acontecimento_importante" rows="3" value={formDataCadete.acontecimento_importante || ''} onChange={handleCadeteChange} className="w-full border p-2 rounded"></textarea>
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700">6. Indique algo realizado pelo Oficial Preceptor que corresponda com um conhecimento adquirido na APMBB:</label>
                <textarea name="indicacao_preceptor" rows="3" value={formDataCadete.indicacao_preceptor || ''} onChange={handleCadeteChange} className="w-full border p-2 rounded"></textarea>
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700">7. Indique eventuais dificuldades que você tenha enfrentado e possíveis formas de solução:</label>
                <textarea name="dificuldades_solucoes" rows="3" value={formDataCadete.dificuldades_solucoes || ''} onChange={handleCadeteChange} className="w-full border p-2 rounded"></textarea>
            </div>
            
            <div>
                <label className="block text-sm font-medium text-gray-700">8. Você indicaria este estágio (nas condições em que realizou) para outro Al Of PM:</label>
                <div className="flex space-x-6 mt-2">
                    <label className="flex items-center">
                        <input type="radio" name="indicaria_estagio" value="SIM" checked={formDataCadete.indicaria_estagio === 'SIM'} onChange={handleCadeteChange} className="h-4 w-4 text-blue-600 border-gray-300" />
                        <span className="ml-2 text-gray-700">SIM</span>
                    </label>
                    <label className="flex items-center">
                        <input type="radio" name="indicaria_estagio" value="NAO" checked={formDataCadete.indicaria_estagio === 'NAO'} onChange={handleCadeteChange} className="h-4 w-4 text-blue-600 border-gray-300" />
                        <span className="ml-2 text-gray-700">NÃO</span>
                    </label>
                </div>
                <p className="text-xs text-gray-500 mt-2">OBS: Este Relatório não exime a elaboração de Parte Circunstanciada diante de eventuais necessidades.</p>
            </div>

            {error && <p className="text-red-600 text-sm mt-4">{error}</p>}
            {reportSuccess && !partialSaveMessage && (
                <div className="mt-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                    <p className="font-bold whitespace-pre-line">{reportSuccess}</p>
                    {whatsappLink && (
                        <a 
                            href={whatsappLink} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="mt-3 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                        >
                            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.008-.57-.008-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                            Enviar para o Oficial (WhatsApp)
                        </a>
                    )}
                </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" onClick={onClose} variant="secondary" disabled={processing}>Cancelar</Button>
                <Button type="submit" variant="success" disabled={processing || !hasCadeteInitialData}>
                    {processing ? "Salvando..." : "Salvar e Finalizar Relatório"}
                </Button>
            </div>
        </form>
    );

    const renderPreceptorForm = () => (
        <div className="space-y-6">
            <h3 className="text-xl font-semibold text-green-600 mb-4">Formulário de Avaliação do Oficial Preceptor</h3>
            {relatorioExistente?.preceptor?.posto_nome && (
                <div className="p-4 bg-green-50 border border-green-300 text-green-800 rounded-lg space-y-2">
                    <p className="font-bold">Avaliação Preenchida!</p>
                    <p className="text-sm">Preceptor: {relatorioExistente.preceptor.posto_nome} (Data: {relatorioExistente.preceptor.data_avaliacao})</p>
                </div>
            )}
            
            <div className="space-y-4">
                <h4 className="font-semibold border-b pb-2">DADOS DO PRECEPTOR</h4>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">E-mail</label>
                        <p className="w-full border p-2 rounded bg-gray-100 text-sm">{formDataPreceptor.email || 'N/A'}</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Posto e nome do Oficial Avaliador</label>
                        <p className="w-full border p-2 rounded bg-gray-100 text-sm">{formDataPreceptor.posto_nome || 'N/A'}</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Telefone</label>
                        <p className="w-full border p-2 rounded bg-gray-100 text-sm">{formDataPreceptor.telefone || 'N/A'}</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Função Executada</label>
                        <p className="w-full border p-2 rounded bg-gray-100 text-sm">{formDataPreceptor.funcao_executada || 'N/A'}</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Período</label>
                        <p className="w-full border p-2 rounded bg-gray-100 text-sm">{formDataPreceptor.periodo || 'N/A'}</p>
                    </div>
                </div>

                <h4 className="font-semibold border-b pb-2 pt-4">AVALIAÇÃO DE QUESITOS (ESCALA 0-10)</h4>
                {ratingQuestions.map(q => (
                    <div key={q.name} className="border p-4 rounded-lg bg-gray-50 space-y-3">
                        <p className="block text-sm font-bold text-gray-700">{q.q}</p>
                        <p className="text-2xl font-extrabold text-center text-blue-600">{formDataPreceptor[q.name] ?? 'Pendente'}</p>
                        <p className="text-sm italic text-gray-700">Comentários: {formDataPreceptor[`${q.name}_comentario`] || 'N/A'}</p>
                    </div>
                ))}
                
                <h4 className="font-semibold border-b pb-2 pt-4">COMENTÁRIOS GERAIS</h4>
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">22) Características positivas de destaque:</label>
                        <p className="w-full border p-2 rounded bg-gray-100 text-sm">{formDataPreceptor.caracteristicas_positivas || 'N/A'}</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">23) Deficiências ou dificuldades observadas:</label>
                        <p className="w-full border p-2 rounded bg-gray-100 text-sm">{formDataPreceptor.deficiencias_dificuldades || 'N/A'}</p>
                    </div>
                </div>
                
                <h4 className="font-semibold border-b pb-2 pt-4">NOTA FINAL</h4>
                <div>
                    <label className="block text-sm font-medium text-gray-700">24) De forma geral, qual nota você daria para o desempenho do Aluno-Oficial no Estágio?</label>
                    <p className="text-3xl font-extrabold text-blue-800">{formDataPreceptor.nota_geral ?? 'Pendente'}</p>
                </div>
            </div>
        </div>
    );
    
    return (
        <>
            <Modal show={show} onClose={() => !processing && onClose()} title={`Relatório de Estágio - ${formatarData(escolha?.data)}`} size="3xl">
                {escolha && (
                    <div className="space-y-4">
                        
                        <div className="flex justify-between items-center p-3 bg-gray-100 rounded-lg border">
                            <h2 className="text-lg font-bold text-gray-800 truncate">Estágio em: {escolha.local_nome} ({escolha.tipo})</h2>
                            
                            <div className="flex space-x-2">
                                <Button 
                                    onClick={() => setActiveForm('cadete')} 
                                    variant={activeForm === 'cadete' ? 'primary' : 'outline'}
                                    className="text-sm"
                                >
                                    Relatório do Cadete
                                </Button>
                                
                                {!isCadeteOrEscalante && (
                                    <Button 
                                        onClick={() => setActiveForm('preceptor')} 
                                        variant={activeForm === 'preceptor' ? 'primary' : 'outline'}
                                        className="text-sm"
                                    >
                                        Avaliação do Preceptor
                                    </Button>
                                )}
                            </div>
                        </div>

                        <div className="max-h-[65vh] overflow-y-auto p-2">
                            {activeForm === 'cadete' && renderCadeteForm()}
                            {activeForm === 'preceptor' && renderPreceptorForm()}
                        </div>
                        
                        <div className="border-t pt-4">
                            <h4 className="text-lg font-semibold text-gray-800 mb-2">Ação do Oficial Preceptor</h4>
                            
                            {/* AQUI É A REGRA: Só mostra se já salvou (relatorioExistente) e se os dados essenciais existem */}
                            {relatorioExistente && hasCadeteInitialData ? (
                                <div className="flex justify-between items-center p-3 bg-yellow-50 border border-yellow-300 rounded-lg">
                                    <p className="text-sm font-medium text-yellow-800">
                                        Clique para gerar um link único para o Oficial Preceptor preencher o formulário:
                                    </p>
                                    <Button 
                                        type="button" 
                                        onClick={() => handleGerarLinkPreceptor(relatorioExistente.id)} 
                                        variant="outline"
                                        className="text-yellow-800 border-yellow-600 hover:bg-yellow-100"
                                    >
                                        Gerar Link
                                    </Button>
                                </div>
                            ) : (
                                <p className="text-sm text-red-600 p-3 bg-red-50 rounded border border-red-200">
                                    ⚠️ **Atenção:** Você deve preencher e **SALVAR** os dados iniciais do estágio (Região, QRA, Telefone, Horários) no formulário acima para liberar a geração do link do Preceptor.
                                </p>
                            )}
                        </div>
                    </div>
                )}
            </Modal>
        </>
    );
};